import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Close } from "@carbon/icons-react";
import "./multiEmailInput.scss";

type MultiEmailInputProps = {
  label: string;
  id: string;
  name: string;
  register: any;
  setValue: any;
  value: string[];
  placeholder?: string;
  required?: boolean;
  errorMessage?: any;
  errorName?: any;
  disabled?: boolean;
};

export default function MultiEmailInput({
  label,
  id,
  name,
  placeholder = "example@email.com",
  errorName,
  errorMessage,
  required,
  register,
  setValue,
  value = [],
  disabled = false,
}: MultiEmailInputProps) {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<string[]>(value);
  const [currentEmail, setCurrentEmail] = useState<string>("");
  const [inputError, setInputError] = useState<string | null>(null);

  // Register the component with react-hook-form
  useEffect(() => {
    register(name);
    setValue(name, value);
  }, [register, name, setValue, value]);

  // Update form value when emails change
  useEffect(() => {
    setValue(name, emails);
  }, [emails, setValue, name]);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const addEmail = () => {
    if (!currentEmail) {
      return;
    }

    if (!validateEmail(currentEmail)) {
      setInputError(t("validations.validEmail"));
      return;
    }

    if (emails.includes(currentEmail)) {
      setInputError(t("deliveries.form.validations.emailExists"));
      return;
    }

    setEmails([...emails, currentEmail]);
    setCurrentEmail("");
    setInputError(null);
  };

  const removeEmail = (index: number) => {
    const updatedEmails = [...emails];
    updatedEmails.splice(index, 1);
    setEmails(updatedEmails);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  };

  return (
    <div className="multi-email">
      <label htmlFor={id} className="multi-email__label">
        {label}
        {required && <span className="multi-email__required"> *</span>}
      </label>

      <div className="multi-email__field">
        {/* Display added emails */}
        {emails.map((email, index) => (
          <span key={index} className="multi-email__chip">
            <span>{email}</span>
            {!disabled && (
              <button
                type="button"
                aria-label={t("cta.remove")}
                onClick={() => removeEmail(index)}
                className="multi-email__chip-remove"
              >
                <Close size={16} />
              </button>
            )}
          </span>
        ))}

        {/* Input for new email */}
        {!disabled && (
          <input
            id={id}
            type="email"
            value={currentEmail}
            onChange={(e) => setCurrentEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => currentEmail && addEmail()}
            placeholder={
              emails.length === 0
                ? placeholder
                : t("deliveries.form.addMoreEmails")
            }
            className="multi-email__input"
          />
        )}
      </div>

      {/* Display validation errors */}
      {inputError && <span className="multi-email__error">{inputError}</span>}
      {errorName && <span className="multi-email__error">{errorMessage}</span>}
      <p className="multi-email__help">{t("deliveries.form.emailsHelp")}</p>
    </div>
  );
}
